import {
  Button,
  Callout,
  Dialog,
  Field,
  Input,
  Pill,
  Segmented,
  SegmentedOption,
  Select,
  Textarea,
  Toggle,
} from '@wtfalch/design';
import {
  activateSieveScript,
  type MailboxNode,
  type MailClient,
  type MailRule,
  type MailRuleAction,
  type MailRuleCondition,
  rulesToSieve,
  saveSieveScript,
  sieveScripts,
  sieveScriptText,
  sieveToRules,
  validateSieveScript,
  walk,
} from '@wtfalch/email/mailbox';
import { useEffect, useState } from 'react';

/** The script the rules are saved as. A script of another name that is
 *  active is somebody else's, and is left alone. */
export const RULES_SCRIPT = 'Agora rules';

const CONDITION_LABELS: Record<MailRuleCondition['kind'], string> = {
  from: 'From contains',
  to: 'To contains',
  cc: 'Cc contains',
  subject: 'Subject contains',
  hasAttachment: 'Has an attachment',
  listId: 'Comes from a mailing list',
};

const ACTION_LABELS: Record<MailRuleAction['kind'], string> = {
  moveTo: 'Move to a folder',
  addKeyword: 'Add a label',
  markRead: 'Mark as read',
  forwardTo: 'Forward a copy to',
  stop: 'Stop applying rules',
};

const hasText = (kind: string) => ['from', 'to', 'cc', 'subject'].includes(kind);

function describeCondition(c: MailRuleCondition): string {
  return 'contains' in c ? `${CONDITION_LABELS[c.kind]} "${c.contains}"` : CONDITION_LABELS[c.kind];
}

function describeAction(a: MailRuleAction): string {
  if (a.kind === 'moveTo') return `Move to ${a.mailbox}`;
  if (a.kind === 'addKeyword') return `Add the label ${a.keyword}`;
  if (a.kind === 'forwardTo') return `Forward a copy to ${a.address}`;
  return ACTION_LABELS[a.kind];
}

/** Every address the enabled rules forward to, in rule order, each once. */
export function forwardAddresses(rules: readonly MailRule[]): string[] {
  const found: string[] = [];
  for (const rule of rules) {
    if (!rule.enabled) continue;
    for (const a of rule.actions) {
      if (a.kind === 'forwardTo' && !found.includes(a.address)) found.push(a.address);
    }
  }
  return found;
}

const blankRule = (): MailRule => ({
  id: crypto.randomUUID(),
  name: '',
  enabled: true,
  match: 'all',
  conditions: [{ kind: 'from', contains: '' }],
  actions: [{ kind: 'markRead' }],
});

const withKind = (kind: MailRuleCondition['kind']): MailRuleCondition =>
  hasText(kind) ? ({ kind, contains: '' } as MailRuleCondition) : ({ kind } as MailRuleCondition);

function actionWithKind(kind: MailRuleAction['kind'], folder: string): MailRuleAction {
  switch (kind) {
    case 'moveTo':
      return { kind, mailbox: folder };
    case 'addKeyword':
      return { kind, keyword: '' };
    case 'forwardTo':
      return { kind, address: '' };
    default:
      return { kind };
  }
}

/**
 * Mail rules (`Mail-rules` board): a list of "if ... then ..." rules and an
 * editor, kept as one Sieve script the server runs. Saving checks the script
 * with the server first, and only then makes it the active one. A script
 * somebody wrote by hand is shown as it is and is not touched until the
 * person chooses Replace.
 */
export function Rules({
  client,
  tree,
}: {
  client: MailClient;
  tree: readonly MailboxNode[] | null;
}) {
  const [loaded, setLoaded] = useState(false);
  const [rules, setRules] = useState<MailRule[]>([]);
  const [scriptId, setScriptId] = useState<string | null>(null);
  // A script already named RULES_SCRIPT, when it is not also the active
  // one this account runs (agora#485): replacing a hand-written active
  // script would otherwise try to create a second script of that name
  // instead of overwriting this one, and collide with it.
  const [ownScriptId, setOwnScriptId] = useState<string | null>(null);
  const [foreign, setForeign] = useState<string | null>(null);
  const [asking, setAsking] = useState(false);
  const [draft, setDraft] = useState<MailRule | null>(null);
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState('');

  const folders = tree ? [...walk(tree)].map((m) => m.path.join('/')) : [];

  useEffect(() => {
    let live = true;
    (async () => {
      const scripts = await sieveScripts(client);
      const active = scripts.find((s) => s.isActive);
      const own = scripts.find((s) => s.name === RULES_SCRIPT);
      const ownId = own?.id ?? null;
      const script = active ?? own;
      if (!script) return { id: null, ownId, rules: [], foreign: null };
      const text = await sieveScriptText(client, script.id);
      const parsed = sieveToRules(text);
      if (parsed) return { id: script.id, ownId, rules: parsed, foreign: null };
      return active
        ? { id: null, ownId, rules: [], foreign: text }
        : { id: null, ownId, rules: [], foreign: null };
    })()
      .then((found) => {
        if (!live) return;
        setScriptId(found.id);
        setOwnScriptId(found.ownId);
        setRules(found.rules);
        setForeign(found.foreign);
        setLoaded(true);
      })
      .catch((err: unknown) => {
        if (live) setError(err instanceof Error ? err.message : String(err));
      });
    return () => {
      live = false;
    };
  }, [client]);

  const save = async () => {
    setError('');
    setSaved('');
    setBusy(true);
    try {
      const text = rulesToSieve(rules);
      await validateSieveScript(client, text);
      // Overwrite a script already named RULES_SCRIPT (`ownScriptId`) even
      // when it was not the one these rules were loaded from (`scriptId`
      // is null right after Replace) -- otherwise this would try to create
      // a second script of the same name and collide with it (agora#485).
      const targetId = scriptId ?? ownScriptId;
      const script = await saveSieveScript(
        client,
        RULES_SCRIPT,
        text,
        targetId ? { scriptId: targetId } : undefined,
      );
      await activateSieveScript(client, script.id);
      setScriptId(script.id);
      setOwnScriptId(script.id);
      setDirty(false);
      setSaved('Your rules are saved and running.');
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  const commitDraft = () => {
    if (!draft) return;
    const problem = draftProblem(draft);
    if (problem) {
      setError(problem);
      return;
    }
    setError('');
    const kept: MailRule = {
      ...draft,
      name: draft.name.trim(),
      actions: draft.actions.map((a) =>
        a.kind === 'forwardTo'
          ? { ...a, address: a.address.trim() }
          : a.kind === 'addKeyword'
            ? { ...a, keyword: a.keyword.trim() }
            : a,
      ),
    };
    setRules((list) =>
      list.some((r) => r.id === kept.id)
        ? list.map((r) => (r.id === kept.id ? kept : r))
        : [...list, kept],
    );
    setDirty(true);
    setSaved('');
    setDraft(null);
  };

  if (!loaded) {
    return error ? (
      <Callout tone="bad">{error}</Callout>
    ) : (
      <p className="text-sm text-muted m-0">Loading your rules…</p>
    );
  }

  if (foreign !== null) {
    return (
      <div className="flex flex-col gap-4 mail-set-form">
        <Callout tone="warn">
          This account runs a filter script that was written by hand. It is shown here as it is.
          Rules made here would replace it.
        </Callout>
        <Textarea readOnly rows={12} mono value={foreign} aria-label="Filter script" />
        <div>
          <Button onPress={() => setAsking(true)}>Replace with my own rules</Button>
        </div>
        {asking && (
          <Dialog
            title="Replace the filter script?"
            onCancel={() => setAsking(false)}
            actions={
              <>
                <Button kind="ghost" onPress={() => setAsking(false)}>
                  Keep it
                </Button>
                <Button
                  kind="primary"
                  onPress={() => {
                    setForeign(null);
                    setAsking(false);
                    setDirty(true);
                  }}
                >
                  Replace
                </Button>
              </>
            }
          >
            When you save your rules they become the active filter and this script stops running. It
            stays stored on the server, switched off.
          </Dialog>
        )}
      </div>
    );
  }

  const forwards = forwardAddresses(rules);

  return (
    <div className="flex flex-col gap-4 mail-set-form">
      <p className="text-sm text-muted m-0">Rules run on mail as it arrives, first to last.</p>
      {rules.length === 0 && !draft && <p className="text-sm text-muted m-0">No rules yet.</p>}
      <ul className="mail-share-list">
        {rules.map((rule) => (
          <li key={rule.id} className="card mail-rule">
            <div className="flex items-center gap-3">
              <span className="font-strong grow min-w-0 truncate">{rule.name}</span>
              {!rule.enabled && <Pill quiet>Off</Pill>}
              <Button size="sm" disabled={busy} onPress={() => setDraft(rule)}>
                Edit
              </Button>
              <Button
                size="sm"
                disabled={busy}
                onPress={() => {
                  setRules((list) => list.filter((r) => r.id !== rule.id));
                  setDirty(true);
                  setSaved('');
                }}
              >
                Delete
              </Button>
            </div>
            <p className="text-xs text-muted m-0">
              {`If ${rule.match === 'all' ? 'all' : 'any'} of: ${rule.conditions.map(describeCondition).join('; ')}`}
            </p>
            <p className="text-xs text-muted m-0">
              {`Then: ${rule.actions.map(describeAction).join('; ')}`}
            </p>
          </li>
        ))}
      </ul>
      {draft ? (
        <RuleEditor
          rule={draft}
          folders={folders}
          onChange={setDraft}
          onDone={commitDraft}
          onCancel={() => {
            setDraft(null);
            setError('');
          }}
        />
      ) : (
        <div>
          <Button onPress={() => setDraft(blankRule())}>New rule</Button>
        </div>
      )}
      {forwards.length > 0 && (
        <Callout tone="warn">
          {`Saving sends a copy of matching mail to ${forwards.join(', ')}.`}
        </Callout>
      )}
      <div className="mail-set-status" role="status">
        {error ? (
          <Callout tone="bad">{error}</Callout>
        ) : saved ? (
          <Callout tone="good">{saved}</Callout>
        ) : null}
      </div>
      <div>
        <Button
          kind="primary"
          disabled={busy || !dirty || draft !== null}
          onPress={() => void save()}
        >
          Save rules
        </Button>
      </div>
    </div>
  );
}

/** Why a rule being edited cannot be kept yet, or ''. */
function draftProblem(rule: MailRule): string {
  if (!rule.name.trim()) return 'Give the rule a name.';
  if (rule.conditions.length === 0) return 'Add a condition.';
  if (rule.actions.length === 0) return 'Add an action.';
  for (const c of rule.conditions) {
    if ('contains' in c && !c.contains.trim()) return `${CONDITION_LABELS[c.kind]} needs a word.`;
  }
  for (const a of rule.actions) {
    if (a.kind === 'moveTo' && !a.mailbox) return 'Pick a folder to move to.';
    if (a.kind === 'addKeyword' && !a.keyword.trim()) return 'Type the label to add.';
    if (a.kind === 'forwardTo' && !/^[^\s@]+@[^\s@]+$/.test(a.address.trim())) {
      return 'Type the address to forward to, like name@example.com.';
    }
  }
  return '';
}

function RuleEditor({
  rule,
  folders,
  onChange,
  onDone,
  onCancel,
}: {
  rule: MailRule;
  folders: readonly string[];
  onChange: (rule: MailRule) => void;
  onDone: () => void;
  onCancel: () => void;
}) {
  const setCondition = (i: number, c: MailRuleCondition) =>
    onChange({ ...rule, conditions: rule.conditions.map((x, j) => (j === i ? c : x)) });
  const setAction = (i: number, a: MailRuleAction) =>
    onChange({ ...rule, actions: rule.actions.map((x, j) => (j === i ? a : x)) });
  const forwards = forwardAddresses([{ ...rule, enabled: true }]);

  return (
    <fieldset className="card mail-rule flex flex-col gap-3" aria-label="Rule">
      <Field label="Name">
        <Input
          block
          value={rule.name}
          onChange={(e) => onChange({ ...rule, name: e.target.value })}
        />
      </Field>
      <Toggle
        label="On"
        checked={rule.enabled}
        onChange={(on) => onChange({ ...rule, enabled: on })}
      />
      <Segmented
        label="Match"
        size="md"
        value={rule.match}
        onChange={(v) => onChange({ ...rule, match: v as 'all' | 'any' })}
      >
        <SegmentedOption value="all">All of these</SegmentedOption>
        <SegmentedOption value="any">Any of these</SegmentedOption>
      </Segmented>
      {rule.conditions.map((c, i) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: rows have no identity of their own
        <div key={i} className="flex items-center gap-2">
          <Select
            size="sm"
            aria-label={`Condition ${i + 1}`}
            value={c.kind}
            onChange={(e) => setCondition(i, withKind(e.target.value as MailRuleCondition['kind']))}
          >
            {(Object.keys(CONDITION_LABELS) as MailRuleCondition['kind'][]).map((k) => (
              <option key={k} value={k}>
                {CONDITION_LABELS[k]}
              </option>
            ))}
          </Select>
          {'contains' in c && (
            <Input
              aria-label={`Condition ${i + 1} text`}
              value={c.contains}
              onChange={(e) => setCondition(i, { ...c, contains: e.target.value })}
            />
          )}
          <Button
            size="sm"
            kind="ghost"
            aria-label={`Remove condition ${i + 1}`}
            onPress={() =>
              onChange({ ...rule, conditions: rule.conditions.filter((_, j) => j !== i) })
            }
          >
            Remove
          </Button>
        </div>
      ))}
      <div>
        <Button
          size="sm"
          onPress={() => onChange({ ...rule, conditions: [...rule.conditions, withKind('from')] })}
        >
          Add a condition
        </Button>
      </div>
      {rule.actions.map((a, i) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: rows have no identity of their own
        <div key={i} className="flex items-center gap-2">
          <Select
            size="sm"
            aria-label={`Action ${i + 1}`}
            value={a.kind}
            onChange={(e) =>
              setAction(
                i,
                actionWithKind(e.target.value as MailRuleAction['kind'], folders[0] ?? ''),
              )
            }
          >
            {(Object.keys(ACTION_LABELS) as MailRuleAction['kind'][]).map((k) => (
              <option key={k} value={k}>
                {ACTION_LABELS[k]}
              </option>
            ))}
          </Select>
          {a.kind === 'moveTo' && (
            <Select
              size="sm"
              aria-label={`Folder for action ${i + 1}`}
              value={a.mailbox}
              onChange={(e) => setAction(i, { kind: 'moveTo', mailbox: e.target.value })}
            >
              {folders.map((f) => (
                <option key={f} value={f}>
                  {f}
                </option>
              ))}
            </Select>
          )}
          {a.kind === 'addKeyword' && (
            <Input
              aria-label={`Label for action ${i + 1}`}
              value={a.keyword}
              onChange={(e) => setAction(i, { kind: 'addKeyword', keyword: e.target.value })}
            />
          )}
          {a.kind === 'forwardTo' && (
            <Input
              aria-label={`Address for action ${i + 1}`}
              type="email"
              value={a.address}
              onChange={(e) => setAction(i, { kind: 'forwardTo', address: e.target.value })}
            />
          )}
          <Button
            size="sm"
            kind="ghost"
            aria-label={`Remove action ${i + 1}`}
            onPress={() => onChange({ ...rule, actions: rule.actions.filter((_, j) => j !== i) })}
          >
            Remove
          </Button>
        </div>
      ))}
      <div>
        <Button
          size="sm"
          onPress={() =>
            onChange({
              ...rule,
              actions: [...rule.actions, actionWithKind('markRead', folders[0] ?? '')],
            })
          }
        >
          Add an action
        </Button>
      </div>
      {forwards.length > 0 && (
        <Callout tone="warn">{`This rule sends a copy of matching mail to ${forwards.join(', ')}.`}</Callout>
      )}
      <div className="flex items-center gap-3">
        <Button kind="primary" onPress={onDone}>
          Keep rule
        </Button>
        <Button kind="ghost" onPress={onCancel}>
          Cancel
        </Button>
      </div>
    </fieldset>
  );
}
